/**
 * Summary
 * This component is responsible for displaying and tracking changes to
 * the displayed UTD. The primary sub-components are the UTD slider (which
 * displays the Ticks and allows the user to adjust the UTD) and the UTD
 * Granularity/Filter which will update the global UTD granularity.
 */

import React, { useMemo } from "react";
import bigInt from "big-integer";
import { ChronosDate } from "../../_helpers";
import { useSelector } from "react-redux";
// import { useTime } from "../../_contexts";

export function UTDBar({
  currentDate,
  displayWidth,
  granularity,
  calendar,
  world,
}) {
  // console.log("WORLD", world);
  return useMemo(() => {
    console.group("=== UTD Render!", currentDate);
    // const { details: world } = useSelector((state) => state.world);
    const unitCount = displayWidth / 15;
    console.log("Units to display", unitCount);
    if (!unitCount) return null;
    const date = new ChronosDate(currentDate, { calendar });
    // console.log('Current Date:', date.asString(), date.toFullObject());

    const worldTime = world?.worldTime || false;

    const segment = date.getTimelineSegment(granularity, {
      unitCountDisplay: unitCount,
    });
    console.log("Current Segment (UTD):", segment);

    let unit = "year";

    switch (granularity) {
      case "aeon":
      case "epoch":
      case "myriad":
      case "millendium":
      case "century":
      case "decade":
      case "year":
        unit = "year";
        break;
      case "day":
        unit = "day";
        break;
      case "hour":
        unit = "hour";
        break;
      case "minute":
        unit = "minute";
        break;
    }
    // console.log('Granularity: ', granularity, ' Display Mode (unit)', unit);

    const dateObject = new ChronosDate(segment.start, {
      calendar,
    }).toFullObject();

    console.log("Date at beginning of segment (UTD)", dateObject);

    let labels = [];
    if (unit === "day") {
      console.log("Generating Day Ticks");
      labels = getDayTicks(unitCount, dateObject, calendar);
    }
    if (unit === "year") {
      console.log(`Generating Year+ Ticks (${granularity})`);
      labels = getYearTicks(
        unitCount,
        granularity,
        dateObject,
        // startingUnit,
        calendar,
        worldTime?.afterZeroAbbr ?? "CE",
        worldTime?.beforeZeroAbbr ?? "BCE"
        // "CE", //TODO: Change these to the world's time settings
        // "BCE"
      );
    }
    if (unit === "hour") {
      console.log("Generating Hour Ticks");
      labels = getHourTicks(unitCount, date, calendar);
    }
    if (unit === "minute") {
      console.log("Generating Minute Ticks");
      labels = getMinuteTicks(unitCount, date, calendar);
    }
    // console.log('Labels toRender', labels);
    console.groupEnd();
    return (
      <div
        className="chronos-chronicle-utd-bar"
        style={{ width: displayWidth, height: "40px" }}
      >
        <svg width={displayWidth} height={40}>
          <g>{labels}</g>
        </svg>
      </div>
    );
  }, [currentDate, calendar, displayWidth, granularity]);
}

function UTDMonth({ month, isLeap, posOffset, monthWidth, skipDays = 0 }) {
  // console.log('Offset', posOffset);
  const days = (isLeap ? month.days + month.leapDays : month.days) - skipDays;
  // console.log('Number of days', days, skipDays);
  //add chronos-utd-leap-day-tick for ticks that are greate
  const segmentClasses = [
    `chronos-utd-month-segment`,
    `chronos-utd-month-${month.name.toLowerCase()}`,
    `chronos-utd-${days}-days`,
  ];
  if (isLeap) segmentClasses.push("chronos-utd-leap-month");
  return (
    <svg
      width={days * 15}
      x={posOffset}
      y={0}
      className={segmentClasses.join(" ")}
    >
      <text
        x={5}
        y={12}
        className={`chronos-utd-month-name`}
        fontSize="0.75rem"
        stroke="#665c46"
      >
        {month.name}
      </text>
      {Array(Math.abs(days))
        .fill(0)
        .map((_, i) => {
          if (i === 0) {
            return (
              <line
                x1={i * 15}
                x2={i * 15}
                y1={0}
                y2={40}
                stroke={"#665c46"}
                key={i}
                className={`chronos-utd-tick chronos-utd-first-tick chronos-utd-day-tick`}
              />
            );
          }
          if ((i + 1) % 5 === 0) {
            const label = skipDays === 0 ? i + 1 : skipDays + i + 1;
            // if ((i + 1) % 5 === 0) {
            //   const label = skipDays === 0 ? i + 1 : skipDays + i + 1;
            const labelClasses = [
              `chronos-utd-tick-label`,
              `chronos-utd-label-${label.toString().toLowerCase()}`,
            ];
            const tallClasses = [
              `chronos-utd-tick`,
              `chronos-utd-tick-lg`,
              `chronos-utd-day-tick`,
              `utd-tick-${i + 1}`,
            ];
            if (i + 1 >= month.days) {
              tallClasses.push("chronos-utd-leap-tick");
              labelClasses.push("chronos-utd-leap-tick-label");
            }
            return (
              // <g x={i * 15} y1={40} y2={0}>
              <React.Fragment key={i}>
                <text
                  x={i * 15 - (label.toString().length * 8) / 2}
                  y={20}
                  stroke="#665c46"
                  fontSize="0.75rem"
                  className={labelClasses.join(" ")}
                >
                  {label}
                </text>
                <line
                  x1={i * 15}
                  x2={i * 15}
                  y1={40}
                  y2={25}
                  stroke={"#665c46"}
                  className={tallClasses.join(" ")}
                />
                {/* </g> */}
              </React.Fragment>
            );
          }
          const stdClasses = [
            `chronos-utd-tick`,
            `chronos-utd-day-tick`,
            `chronos-utd-tick-${i + 1}`,
          ];
          if (i + 1 >= month.days) stdClasses.push("chronos-utd-leap-tick");
          return (
            <line
              x1={i * 15}
              x2={i * 15}
              y1={40}
              y2={30}
              stroke={"#665c46"}
              key={i}
              className={stdClasses.join(" ")}
            />
          );
        })}
    </svg>
  );
}

function getDayTicks(unitCount, startDate, calendar) {
  // console.warn('-------');
  // console.group('--Start off getDayTicks');
  // console.log('unitCount', unitCount, 'startDate (object)', startDate);
  const { months } = calendar;
  const relativeDate = new ChronosDate("0", { calendar }).toRelativeDate(
    startDate
  );

  // console.log('Relative Start Date', { ...relativeDate });

  const leapMonths = months.filter((m) => m.leapPeriod > 0 && m.leapDays > 0);
  const cycleLength = leapMonths.reduce((t, month) => {
    return t * month.leapPeriod;
  }, 1);

  const remainingYears = bigInt(relativeDate.year).mod(cycleLength);

  let ret = [];
  let lastPosX = 0;
  let shouldBreak = false;
  for (let c = relativeDate.year; !shouldBreak; c++) {
    c = c % cycleLength;
    // console.log('C after', c);
    for (let m = relativeDate.month; !shouldBreak; m++) {
      if (m >= months.length) {
        m = m % months.length;
        c += 1;
      }

      const month = months[m];
      // console.log('Month', month.name);
      const shouldSkip =
        relativeDate.year % cycleLength === c && relativeDate.month === m; //--> then skip days
      const isLeapYear = c % month.leapPeriod === 0;
      const skipDays = shouldSkip ? relativeDate.day - 1 : 0; // -1 for left side of the next item
      const daysToCompare = isLeapYear
        ? month.days + month.leapDays
        : month.days;
      // console.log('c', c, 'm', m, isLeapYear, 'Skip Days', skipDays);
      if (unitCount > daysToCompare) {
        // console.log('Month is less than unitCount', daysToCompare, unitCount);
        unitCount -= daysToCompare - skipDays;
        const monthWidth = (daysToCompare - skipDays) * 15;
        let posX = lastPosX;
        lastPosX = lastPosX + monthWidth;
        // console.log(c, posX);
        ret.push(
          <UTDMonth
            month={month}
            isLeap={isLeapYear}
            posOffset={posX}
            monthWidth={monthWidth}
            key={`${c}_${month.name.toLowerCase()}}_${Math.random() * 10000}`}
            skipDays={skipDays}
          />
        );
      } else {
        // console.log('Days Remaining', unitCount);
        shouldBreak = true;
        if (m > months.length) {
          m = m % months.length;
          year += 1;
        }
        const month = months[m];
        const isLeapYear = (remainingYears - c) % month.leapPeriod == 0;
        const daysToCompare = isLeapYear
          ? month.days + month.leapDays
          : month.days;
        const monthWidth = daysToCompare * 15;
        ret.push(
          <UTDMonth
            month={month}
            isLeap={isLeapYear}
            posOffset={lastPosX}
            monthWidth={monthWidth}
            key={`${c}_${month.name.toLowerCase()}_${Math.random() * 10000}`}
          />
        );
      }
    }
  }

  // console.groupEnd();
  return ret;
}

function leapDaysInYear(year, calendar) {
  let leapDays = 0;
  const { months } = calendar;
  for (let m = 0; m < months.length; m++) {
    if (months[m].leapPeriod > 0 && months[m].leapDays > 0) {
      if (year % months[m].leapPeriod === 0) {
        leapDays += months[m].leapDays;
      }
    }
  }
  return leapDays;
}

function getYearTicks(
  unitCount,
  granularity,
  date,
  // year,
  calendar,
  posSuffix,
  negSuffix
) {
  let ret = [];
  const { year } = date;
  console.log("Calculating Year Ticks", year);
  const unitDuration = calendar.time[granularity].duration;
  const yearsInUnit = bigInt(unitDuration)
    .divide(calendar.time.year.duration)
    .toJSNumber();
  for (let u = 0; u < unitCount; u++) {
    const yearsToAdd = yearsInUnit * u;
    let yearLabel = (year + yearsToAdd + 0).toString().replace(/\-/g, "");
    const isNegative = year + yearsToAdd + 0 < 0;
    const isLeap = leapDaysInYear(year + yearsToAdd + 0, calendar) > 0;
    if (!isNegative && posSuffix && posSuffix.length > 0) {
      yearLabel = yearLabel + ` ${posSuffix}`;
    }
    if (isNegative && negSuffix && negSuffix.length > 0) {
      yearLabel = yearLabel + ` ${negSuffix}`;
    }

    let labelClasses = ["chronos-utd-tick-label", "chronos-utd-year-label"];
    let tickClasses = [
      "chronos-utd-tick",
      "chronos-utd-year-tick",
      `chronos-utd-year-${(year + yearsToAdd + 0).toString()}`,
    ];
    if (isLeap) {
      labelClasses.push("chronos-utd-leap-year-label");
      tickClasses.push("chronos-utd-leap-year-tick");
    }
    const isTall = u !== 0 && u % 5 === 0;
    ret.push(
      <g key={u}>
        {isTall && (
          <text
            x={u * 15 - (yearLabel.length * 8) / 2}
            y={20}
            stroke="#665c46"
            fontSize="0.75rem"
          >
            {yearLabel}
          </text>
        )}
        <line
          x1={u * 15}
          x2={u * 15}
          y1={40}
          y2={isTall ? 25 : 30}
          stroke={isTall ? "#665c46" : "#665c46"} //darker, lighter
          key={u}
        />
      </g>
    );
  }

  return ret;
}

function getMinuteTicks(unitCount, currentDate, calendar) {
  const { time } = calendar;
  const minutesInHour = bigInt(time.hour.duration)
    .divide(time.minute.duration)
    .toJSNumber();
  const segment = currentDate.getTimelineSegment("minute");
  // console.log('Minute Segment', segment);
  let relativeStartDate = new ChronosDate(segment.start, {
    calendar,
  }).toRelativeDate();
  let ret = [];
  let lastPosX = 0;
  let shouldBreak = false;

  while (!shouldBreak) {
    if (lastPosX === 0) {
      const skipMinutes = relativeStartDate.minute;
      const width = (minutesInHour - skipMinutes) * 15;
      ret.push(
        <UTDHourBlock
          key={lastPosX}
          width={width}
          date={{ ...relativeStartDate }}
          position={lastPosX}
          unitsToDisplay={minutesInHour}
          skipMinutes={skipMinutes}
          calendar={calendar}
        />
      );
      relativeStartDate = new ChronosDate("0", { calendar }).addToRelativeDate(
        relativeStartDate,
        "hour",
        1
      );
      unitCount -= minutesInHour - skipMinutes;
      lastPosX += width;
      continue;
    }
    if (unitCount >= minutesInHour) {
      const width = minutesInHour * 15;
      ret.push(
        <UTDHourBlock
          key={lastPosX}
          width={width}
          date={{ ...relativeStartDate }}
          position={lastPosX}
          unitsToDisplay={minutesInHour}
          calendar={calendar}
        />
      );
      lastPosX += width;
    } else {
      const width = unitCount * 15;
      ret.push(
        <UTDHourBlock
          key={lastPosX}
          width={width}
          position={lastPosX}
          unitsToDisplay={unitCount}
          date={{ ...relativeStartDate }}
          calendar={calendar}
        />
      );
      shouldBreak = true;
      lastPosX += width;
    }
    unitCount -= minutesInHour;
    if (unitCount <= 0) {
      shouldBreak = true;
    }

    relativeStartDate = new ChronosDate("0", { calendar }).addToRelativeDate(
      relativeStartDate,
      "hour",
      1
    );
  }
  return ret;
}

function getHourTicks(unitCount, currentDate, calendar) {
  const { time } = calendar;
  const hoursInDay = bigInt(time.day.duration)
    .divide(time.hour.duration)
    .toJSNumber();
  // const relativeDate = new ChronosDate('0', { calendar }).toRelativeDate(startDate);
  const segment = currentDate.getTimelineSegment("hour");
  // console.log('Hour Segment', segment);
  let relativeStartDate = new ChronosDate(segment.start, {
    calendar,
  }).toRelativeDate();
  // const skipHours = relativeStartDate.hour > 0 ? relativeStartDate - 1 : 0;
  // console.log('Starting Relative Date', relativeStartDate);
  let shouldBreak;
  let lastPosX = 0;
  let ret = [];
  // return ret;
  while (!shouldBreak) {
    if (lastPosX == 0) {
      // skip X amount of hours at the start, add block, continue
      const skipHours = relativeStartDate.hour;
      const width = (hoursInDay - skipHours) * 15;
      ret.push(
        <UTDDayBlock
          key={lastPosX}
          width={width}
          position={lastPosX * width}
          unitsToDisplay={hoursInDay}
          skipHours={skipHours}
          date={{ ...relativeStartDate }}
          calendar={calendar}
        />
      );
      relativeStartDate = new ChronosDate("0", { calendar }).addToRelativeDate(
        relativeStartDate,
        "day",
        1
      );
      unitCount -= hoursInDay - skipHours;
      lastPosX += width;
      continue;
    }
    if (unitCount >= hoursInDay) {
      const width = hoursInDay * 15;
      ret.push(
        <UTDDayBlock
          key={lastPosX}
          width={width}
          date={{ ...relativeStartDate }}
          position={lastPosX}
          unitsToDisplay={hoursInDay}
          calendar={calendar}
        />
      );
      lastPosX += width;
    } else {
      const width = unitCount * 15;
      ret.push(
        <UTDDayBlock
          key={lastPosX}
          width={width}
          position={lastPosX}
          unitsToDisplay={unitCount}
          date={{ ...relativeStartDate }}
          calendar={calendar}
        />
      );
      shouldBreak = true;
      lastPosX += width;
    }
    //Finally, remove the number of hours in a day from the total count
    unitCount -= hoursInDay;
    if (unitCount < 0) {
      shouldBreak = true;
    }

    relativeStartDate = new ChronosDate("0", { calendar }).addToRelativeDate(
      relativeStartDate,
      "day",
      1
    );
  }
  return ret;
}

function UTDHourBlock({
  position,
  width,
  unitsToDisplay,
  date,
  calendar,
  skipMinutes = 0,
}) {
  let segmentClasses = ["chronos-utd-hour-segment"];
  const showDate = date !== undefined && calendar !== undefined;
  let displayDate = "";
  if (showDate) {
    const { months } = calendar;
    const { year, month, day, hour } = date;
    const monthName = months[month].name;

    displayDate = `${monthName} ${Math.abs(day)} ${Math.abs(year)} ${Math.abs(
      hour
    )}:00`;
  }

  const unitCount = unitsToDisplay - skipMinutes;

  return (
    <svg width={width} x={position} y={0} className={segmentClasses.join(" ")}>
      <text x={5} y={12} stroke="#665c46" fontSize="0.75rem">
        {showDate && displayDate !== "" && displayDate}
      </text>
      {Array(unitCount)
        .fill(0)
        .map((minute, i) => {
          const minuteLabel = `${date.hour}:${i + skipMinutes}`;
          if (i === 0) {
            // console.log(unitCount);
            return (
              <line
                x1={i * 15}
                x2={i * 15}
                y1={0}
                y2={40}
                stroke={"#665c46"}
                key={i}
              />
            );
          }

          if ((i + skipMinutes) % 5 === 0) {
            return (
              <React.Fragment key={i}>
                <text
                  x={i * 15 - (minuteLabel.length * 6) / 2}
                  y={20}
                  stroke={"#665c46"}
                  fontSize="0.75rem"
                  className={`chronos-utd-minute-name`}
                >
                  {minuteLabel}
                </text>
                <line
                  x1={i * 15}
                  x2={i * 15}
                  y1={40}
                  y2={25}
                  stroke={"#665c46"}
                />
              </React.Fragment>
            );
          }

          return (
            <line
              x1={i * 15}
              x2={i * 15}
              y1={40}
              y2={30}
              stroke={"#665c46"}
              key={i}
            />
          );
        })}
    </svg>
  );
}

function UTDDayBlock({
  position,
  width,
  unitsToDisplay,
  date,
  calendar,
  skipHours = 0,
}) {
  let segmentClasses = ["chronos-utd-day-segment"];
  const showDate = date !== undefined && calendar !== undefined;
  let displayDate = "";
  if (showDate) {
    const { day, month, year } = date;
    const { months } = calendar;
    const monthName = months[month].name;
    console.log("Year value", year);
    displayDate = `${monthName} ${day}, ${bigInt(year).abs().toString()}`;
  }
  const unitCount = unitsToDisplay - skipHours;
  return (
    <svg width={width} x={position} y={0} className={segmentClasses.join(" ")}>
      <text x={5} y={12} stroke="#665c46" fontSize="0.75rem">
        {showDate && displayDate !== "" && displayDate}
        {/* {unitsToDisplay} Hours */}
      </text>
      {Array(unitCount)
        .fill(0)
        .map((hour, i) => {
          let hourLabel = `${i + skipHours}:00`;
          if (i === 0) {
            return (
              <line
                x1={i * 15}
                x2={i * 15}
                y1={0}
                y2={40}
                stroke={"#665c46"}
                key={i}
              />
            );
          }

          if ((i + skipHours + 1) % 6 === 1) {
            return (
              <React.Fragment key={i}>
                <text
                  x={i * 15 - (hourLabel.length * 6) / 2}
                  y={23}
                  stroke="#665c46"
                  fontSize="0.75rem"
                >
                  {hourLabel}
                </text>
                <line
                  x1={i * 15}
                  x2={i * 15}
                  y1={40}
                  y2={25}
                  stroke={"#665c46"}
                />
              </React.Fragment>
            );
          }
          return (
            <line
              x1={i * 15}
              x2={i * 15}
              y1={40}
              y2={30}
              stroke={"#665c46"}
              key={i}
            />
          );
        })}
    </svg>
  );
}
